/**
 * The settings screen's help-and-about card.
 *
 * All four destinations are external URLs on the marketing site, copied
 * from the phone app, so every row carries the external-link affordance
 * rather than the internal chevron. The version line is deliberately not
 * interactive.
 */

import { Card, cn, HIT_SLOP } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  buildVersionLine,
  EXTERNAL_LINK_HINT,
  HELP_AND_ABOUT_TITLE,
  HELP_ROWS,
} from "./helpAndAboutCopy";

const ExternalLinkIcon = (): ReactNode => (
  <svg
    data-testid="external-link-icon"
    role="img"
    aria-label={EXTERNAL_LINK_HINT}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    className="h-icon-sm w-icon-sm shrink-0 text-ink-secondary"
  >
    <path d="M12 8.5V12a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1h3.5" />
    <path d="M9.5 3H13v3.5" />
    <path d="M13 3L8 8" />
  </svg>
);

export const HelpAndAboutCard = (): ReactNode => (
  <Card title={HELP_AND_ABOUT_TITLE} data-testid="help-and-about-card">
    <div className="flex flex-col">
      {HELP_ROWS.map((row) => (
        <a
          key={row.key}
          href={row.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`help-row-${row.key}`}
          // `relative` keeps HIT_SLOP's overlay inside this row (ISSUE-971). Without it the
          // overlay escapes to the page and covers the Blocked list link at laptop heights.
          className={cn(
            "relative flex items-center justify-between gap-2 py-3 text-body text-ink",
            HIT_SLOP,
          )}
        >
          <span>{row.label}</span>
          <ExternalLinkIcon />
        </a>
      ))}
      <p
        data-testid="help-version-line"
        className="py-3 text-caption text-ink-secondary"
      >
        {buildVersionLine()}
      </p>
    </div>
  </Card>
);
